import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import { Alert, Box, Button, IconButton, Stack, Switch, TextField, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRowSelectionModel, GridToolbarContainer } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ADMIN_TABLE_DEFAULT_PAGE_SIZE, ADMIN_TABLE_PAGE_SIZE_OPTIONS } from 'constants/adminTables';
import { useSnackbar } from 'notistack';
import React, { useMemo, useState } from 'react';

import { bulkUpdateJobPresetLifecycle, getJobPresetsForAdmin, JobPresetLifecycleUpdate } from 'api/jobPreset';
import { DownloadCsvButton } from 'components/atoms/BaseDataGrid/DownloadCsvButton';
import { PresetAuthoringMode } from 'components/StudyDashboard/ProceduresPage/Actions/PostProcessingAndCalculateFeatures/PresetAuthoringMode';
import { JobPresetAdmin } from 'interfaces/jobPreset';
import { Permission } from 'interfaces/permissionOption';
import { formatDateTimeForUser } from 'utils/dateTime';
import { usePermissions } from 'utils/usePermissions';

type PendingLifecycle = Pick<JobPresetLifecycleUpdate, 'isPublished' | 'isGlobal'>;

const mergePending = (preset: JobPresetAdmin, pending?: PendingLifecycle): JobPresetAdmin => ({
  ...preset,
  ...pending,
});

const PresetsCsvToolbar = () => (
  <GridToolbarContainer>
    <DownloadCsvButton />
  </GridToolbarContainer>
);

interface PresetsDataGridProps {
  onAuthoringChange?: (isAuthoring: boolean) => void;
}

export const PresetsDataGrid: React.FC<PresetsDataGridProps> = ({ onAuthoringChange }) => {
  const { hasPermission } = usePermissions();
  const canPublish = hasPermission(Permission.PublishCalculateFeaturesPreset);
  const canChangeGlobal = hasPermission(Permission.MakeCalculateFeaturesPresetGlobal);
  const canAuthorGlobalPresets = canPublish && canChangeGlobal;
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [selection, setSelection] = useState<GridRowSelectionModel>([]);
  const [search, setSearch] = useState('');
  const [pending, setPending] = useState<Record<string, PendingLifecycle>>({});
  const [authoringPreset, setAuthoringPreset] = useState<JobPresetAdmin | 'new' | null>(null);

  React.useEffect(() => {
    onAuthoringChange?.(Boolean(authoringPreset));
    return () => onAuthoringChange?.(false);
  }, [authoringPreset, onAuthoringChange]);

  const presetsQuery = useQuery(['jobPresets', 'admin'], ({ signal }) => getJobPresetsForAdmin(signal));
  const rows = useMemo(
    () => (presetsQuery.data || []).map((preset) => mergePending(preset, pending[preset.id])),
    [pending, presetsQuery.data]
  );

  const stage = (ids: GridRowSelectionModel, change: PendingLifecycle) => {
    setPending((current) => {
      const next = { ...current };
      ids.forEach((id) => {
        next[String(id)] = { ...next[String(id)], ...change };
      });
      return next;
    });
  };

  const saveMutation = useMutation({
    mutationFn: () =>
      bulkUpdateJobPresetLifecycle(
        Object.entries(pending).map(([id, lifecycle]) => ({
          id,
          ...lifecycle,
        }))
      ),
    onSuccess: (updatedPresets) => {
      queryClient.setQueryData<JobPresetAdmin[]>(['jobPresets', 'admin'], (current = []) => {
        const updatedById = new Map(updatedPresets.map((preset) => [preset.id, preset]));
        return current.map((preset) => updatedById.get(preset.id) || preset);
      });
      setPending({});
      enqueueSnackbar('Preset visibility changes saved', { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar('Preset visibility changes were not saved', { variant: 'error' });
    },
  });

  const columns = useMemo<GridColDef<JobPresetAdmin>[]>(
    () => [
      { field: 'name', headerName: 'Name', flex: 1.4, minWidth: 180 },
      { field: 'versionNumber', headerName: 'Version', width: 95 },
      {
        field: 'sourceStudyId',
        headerName: 'Source study',
        flex: 1,
        minWidth: 170,
        valueGetter: (_, row) =>
          row.sourceStudyId ? row.sourceStudyName || 'Deleted or unavailable study' : 'Not assigned',
      },
      {
        field: 'createdByName',
        headerName: 'Owner',
        flex: 1,
        minWidth: 170,
        valueGetter: (_, row) => row.createdByName || 'Unknown user',
      },
      {
        field: 'isPublished',
        headerName: 'Published',
        width: 120,
        sortable: true,
        renderCell: ({ row }) => (
          <Switch
            size="small"
            checked={row.isPublished}
            disabled={!canPublish || saveMutation.isLoading}
            inputProps={{ 'aria-label': `Published ${row.name}` }}
            onChange={(_, checked) => stage([row.id], { isPublished: checked })}
          />
        ),
      },
      {
        field: 'isGlobal',
        headerName: 'Global',
        width: 105,
        sortable: true,
        renderCell: ({ row }) => (
          <Switch
            size="small"
            checked={row.isGlobal}
            disabled={!canChangeGlobal || saveMutation.isLoading}
            inputProps={{ 'aria-label': `Global ${row.name}` }}
            onChange={(_, checked) => stage([row.id], { isGlobal: checked })}
          />
        ),
      },
      {
        field: 'updatedAt',
        headerName: 'Updated',
        flex: 1,
        minWidth: 170,
        valueGetter: (_, row) => row.updatedAt || row.createdAt,
        valueFormatter: (value) => (value ? formatDateTimeForUser(value) : ''),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 90,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Tooltip title="Edit preset">
            <span>
              <IconButton
                size="small"
                aria-label={`Edit ${row.name}`}
                disabled={!canAuthorGlobalPresets}
                onClick={() => setAuthoringPreset(row)}
              >
                <EditRoundedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        ),
      },
    ],
    [canAuthorGlobalPresets, canChangeGlobal, canPublish, saveMutation.isLoading]
  );

  const selectedCount = selection.length;
  const hasChanges = Object.keys(pending).length > 0;

  if (authoringPreset) {
    return (
      <PresetAuthoringMode
        preset={authoringPreset === 'new' ? undefined : authoringPreset}
        onClose={() => setAuthoringPreset(null)}
      />
    );
  }

  return (
    <Stack spacing={2}>
      {canAuthorGlobalPresets && (
        <Stack direction="row" justifyContent="flex-end">
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setAuthoringPreset('new')}>
            Create preset
          </Button>
        </Stack>
      )}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
        <TextField
          label="Search presets"
          size="small"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ width: { xs: '100%', md: 320 } }}
        />
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {canPublish && (
            <>
              <Button
                size="small"
                variant="outlined"
                startIcon={<VisibilityRoundedIcon />}
                disabled={!selectedCount}
                onClick={() => stage(selection, { isPublished: true })}
              >
                Publish
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<VisibilityOffRoundedIcon />}
                disabled={!selectedCount}
                onClick={() => stage(selection, { isPublished: false })}
              >
                Unpublish
              </Button>
            </>
          )}
          {canChangeGlobal && (
            <>
              <Button
                size="small"
                variant="outlined"
                startIcon={<PublicRoundedIcon />}
                disabled={!selectedCount}
                onClick={() => stage(selection, { isGlobal: true })}
              >
                Make global
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={!selectedCount}
                onClick={() => stage(selection, { isGlobal: false })}
              >
                Make study-specific
              </Button>
            </>
          )}
        </Stack>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="contained"
          startIcon={<CheckRoundedIcon />}
          disabled={!hasChanges || saveMutation.isLoading}
          onClick={() => saveMutation.mutate()}
        >
          Save changes
        </Button>
      </Stack>

      {hasChanges && (
        <Alert severity="info">
          {Object.keys(pending).length} preset {Object.keys(pending).length === 1 ? 'has' : 'have'} unsaved changes.
        </Alert>
      )}

      <Box sx={{ width: '100%' }}>
        <DataGrid
          autoHeight
          rows={rows}
          columns={columns}
          loading={presetsQuery.isLoading}
          checkboxSelection
          disableRowSelectionOnClick
          rowSelectionModel={selection}
          onRowSelectionModelChange={setSelection}
          filterModel={{ items: [], quickFilterValues: [search] }}
          initialState={{
            pagination: { paginationModel: { pageSize: ADMIN_TABLE_DEFAULT_PAGE_SIZE } },
            sorting: { sortModel: [{ field: 'updatedAt', sort: 'desc' }] },
          }}
          pageSizeOptions={ADMIN_TABLE_PAGE_SIZE_OPTIONS}
          slots={{ toolbar: PresetsCsvToolbar }}
          localeText={{
            noRowsLabel: presetsQuery.isError
              ? 'Presets could not be loaded'
              : 'No active calculate-features presets found',
          }}
        />
      </Box>
      {presetsQuery.isError && (
        <Typography color="error">Preset management data could not be loaded. Please try again.</Typography>
      )}
    </Stack>
  );
};
